/**
 * AttachmentTile - Display tile for attachments in unified list
 *
 * Same visual style as sub-item tiles but shows attachment info.
 * Click to open attachment, delete button on hover.
 */

import { Paperclip, Trash2, FileIcon, ImageIcon, FileTextIcon } from 'lucide-react';
import type { Attachment } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface AttachmentTileProps {
  attachment: Attachment;
  onDelete: (attachment: Attachment) => void;
  disabled?: boolean;
  isPending?: boolean;
}

interface PendingAttachmentTileProps {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return FileIcon;
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.includes('pdf') || mimeType.includes('text')) return FileTextIcon;
  return FileIcon;
}

// ============================================================================
// SAVED ATTACHMENT TILE
// ============================================================================

export function AttachmentTile({
  attachment,
  onDelete,
  disabled = false,
}: AttachmentTileProps) {
  const Icon = getFileIcon(attachment.mimeType);

  return (
    <div className="flex items-center gap-3 rounded-lg group">
      {/* Icon */}
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-shrink-0"
      >
        <Icon className="h-5 w-5 text-gray-500" />
      </a>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <p className="font-medium truncate text-gray-900 hover:text-blue-600">
            {attachment.name}
          </p>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <Paperclip className="h-3 w-3" />
            {formatFileSize(attachment.size)}
          </p>
        </a>
      </div>

      {/* Actions */}
      {!disabled && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onDelete(attachment)}
            className="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PENDING ATTACHMENT TILE (for files not yet uploaded)
// ============================================================================

export function PendingAttachmentTile({
  file,
  onRemove,
  disabled = false,
}: PendingAttachmentTileProps) {
  const Icon = getFileIcon(file.type);

  return (
    <div className="flex items-center gap-3 rounded-lg group">
      {/* Icon */}
      <Icon className="h-5 w-5 text-blue-600" />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate text-gray-900">{file.name}</p>
          <span className="text-xs text-blue-600 font-medium">(pending)</span>
        </div>
        <p className="text-sm text-gray-500 flex items-center gap-1">
          <Paperclip className="h-3 w-3" />
          {formatFileSize(file.size)}
        </p>
      </div>

      {/* Actions */}
      {!disabled && (
        <div className="flex items-center gap-1">
          <button
            onClick={onRemove}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
            title="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
